// Copy of the time-tracking walkthrough. The rules every text follows are in ../../de/help.js.
export default {
  title: "Time tracking",
  image: {
    certificate: "Example of a monthly statement as a PDF",
  },
  section: {
    TimeTrackingMy: "My hours",
    TimeTrackingReview: "Review",
    SettingsOverview: "Working-time models",
    EmployeeDetail: "Assignment",
    ProjectDetail: "Project",
    PersonnelCalendar: "Personnel planning",
  },
  related: "Connections",
  step: {
    nav: { text: "This strip is the navigation of time tracking. Each tab opens an area of its own." },
    "tab-my": { text: "Here you record and correct your own week." },
    "tab-review": {
      text: "Here you check your employees' hours, approve them and see who is on the clock right now.",
    },

    "review-filters": {
      text: "Here you choose employees, period, status and project. The grid below shows only what matches.",
    },
    clock: { text: "The clock starts and stops recording. It shows whether a recording is running." },
    "clock-project": { text: "Before clocking in you pick the project. A few letters are enough to find it." },
    "clock-chip": {
      text: "While the clock runs, it sits in the bar at the top, on every page. You can clock out from there too.",
    },
    "clock-break": {
      text: "Break pauses the clock. Its start and end then appear in the record, which is what the working-time act asks for.",
    },
    "clock-in": { text: "Clock in starts the timer. Clocking out turns it into the day's entry." },
    "entry-open": {
      text: "Record time lets you enter a day by hand. Everything it needs is in this window.",
    },
    "entry-basics": {
      text: "Enter the date, start, end and project here. The project is already filled in from personnel planning for that day, and a few letters change it.",
    },
    "entry-break": { text: "A break is one click. The figures below work the day out again straight away." },
    "entry-note": {
      text: "A note explains the day. Save records it, but here we cancel, since this is sample data.",
    },
    export: {
      text: "This menu downloads your hours as a CSV file. A click on Monthly statement produces your month as a PDF.",
    },
    "export-certificate": {
      text: "The monthly statement lists every day of the month with totals and the account. You and your supervisor sign underneath.",
    },
    week: { text: "Everything on this page belongs to one week. The arrows move back and forth." },
    "week-back": {
      text: "One step back shows the previous week. Tiles, list and chart all follow the chosen week.",
    },
    "stat-actual": { text: "Actual time is everything recorded this week." },
    "stat-target": { text: "Target time comes from your work-time model." },
    "stat-balance": { text: "The balance is actual minus target. Red means hours are still missing." },
    "stat-days": { text: "This many days are recorded this week. The number of entries is below." },
    account: { text: "The balance of your hours account across all weeks." },
    entries: { text: "The individual days of the week are listed here." },
    "entry-row": {
      text: "Each row shows times, project, break and balance. On the right, whether the day is approved yet.",
    },
    chart: { text: "The chart shows hours per day against the target." },
    "chart-filter": {
      text: "Clicking a day in the chart narrows the list on the left to that day. A second click lifts the filter again.",
    },
    projects: { text: "Here the week is split across projects." },
    "project-filter": {
      text: "Clicking a project filters the list on the left too. It shows at a glance which days you were there.",
    },
    matrix: {
      text: "One row per employee, one column per day of the period. Where something is missing shows at a glance.",
    },
    "matrix-mode": {
      text: "This switches what the cells show. It starts on Hours, what was recorded per day.",
    },
    "mode-balance": {
      text: "Balance shows each day's difference to the target. Green is above it, orange below.",
    },
    "mode-status": {
      text: "Status shows how far each day has got. S means submitted, A approved and R rejected.",
    },
    "matrix-violations": {
      text: "Read as violations, the matrix shows the days against the working time act. The § in the corner stays in the other readings too.",
    },
    "matrix-day-head": {
      text: "The date above a column is a button. A click judges the whole day, every entry in that column at once.",
    },
    "matrix-create": {
      text: "Under the pointer, a day without an entry shows a plus. A click opens the window for that person and that day.",
    },
    "matrix-absence": {
      text: "A day with an absence shows it at the top of the window, a paid one with its credit. Remove day takes out only this one day.",
    },
    "matrix-pick-ctrl": {
      text: "Hold Ctrl to mark single cells, with gaps between them if you like. A second Ctrl-click takes a cell out again.",
    },
    "matrix-pick-shift": {
      text: "Shift takes the whole rectangle up to the clicked cell, across several people too. The bar below approves the entries in it or enters an absence.",
    },
    "matrix-cell": {
      text: "A click on a cell opens the day: times, break, project, how it was recorded, the review note, and the buttons to approve, reject and correct.",
    },
    total: { text: "The foot of the matrix carries the period's total. That is the figure you approve." },
    "review-crew-open": {
      text: "The window is already set to this person and this day. The Crew entry button above opens the same window with nothing picked.",
    },
    "review-absence-open": {
      text: "The same window has two tabs. Under Absence you enter a holiday, sick leave or an appointment, just as in personnel planning.",
    },
    "review-absence-kind": {
      text: "Pick the kind and how much of the day. A full day, half a day, or from and to for an appointment.",
    },
    "review-absence-rules": {
      text: "Paid absences count on the account as if worked. If one does not fit beside an existing one on a day, the window asks before saving.",
    },
    "review-crew": {
      text: "Other people get the same times, such as a whole crew. The project comes from personnel planning.",
    },
    "review-crew-save": {
      text: "Record creates the times for everyone. The gap in the grid is filled afterwards.",
    },
    "review-approve": {
      text: "This approves every not-yet-approved hour of the chosen people in the period at once, including any a filter is hiding, but not ones you recorded yourself. You are asked to confirm first.",
    },
    "review-export": { text: "The period exports as a CSV file, for payroll for example." },
    "attendance-bar": {
      text: "At the very top is who is clocked in right now. A click on the bar unfolds the names.",
    },
    "attendance-stale": { text: "Anyone who has not clocked out since an earlier day is at the very top, marked." },
    "only-gaps": {
      text: "Only with missing days keeps the people missing a day in the period, the most first. That used to be a page of its own.",
    },
    "account-bar": {
      text: "The time account is the last two columns of every row. They follow the page's period, but not the status and project filters.",
    },
    "account-table": {
      text: "The carry-over is what the person brought into the period. The standing is where they end up: carry-over plus balance.",
    },
    "account-row": {
      text: "A click on the name opens that person's whole account: the standing large, and the figures it is made of under it. Paid absences appear there as a credit.",
    },
    "account-months": {
      text: "Under it, the account month by month. The symbol at the end of each line produces that month's statement as a PDF.",
    },
    "account-certificate": {
      text: "This is what the statement looks like. It lists every day of the month with totals and the account, signed by both sides underneath.",
    },
    "account-export": { text: "The export downloads the accounts as a CSV file, for payroll for example." },
    again: { text: "This button is on every page that has an explanation. One click starts it again." },
    models: {
      text: "Time tracking computes targets, balances and breaks from these models. Which model applies to whom is set on the employee. The model marked “Default” is prefilled when an employee is created.",
    },
    "model-limits": {
      text: "The entry form refuses a day above the daily maximum, and a clocked one appears under Violations. The rounding applies to every day's working time.",
    },
    "model-days": {
      text: "The target per weekday is the target in My hours and in Review. A day with no target is not a working day; a day with a target and no entry counts as missing there.",
    },
    "model-breaks": {
      text: "The break rules deduct the minimum break, even when less was entered. If too little break was taken, the day appears under Violations.",
    },
    "employee-tabs": { text: "You assign the working-time model on the employee, in the Company relevant tab." },
    assignment: {
      text: "Every day is measured against the model in force on that day. Without an assignment a day has no target, even when there is a default model. The opening balance below is what his time account carries in.",
    },
    "project-times": {
      text: "The hours recorded on a project show on the project itself, in the Times register. They are totalled there by employee and by phase.",
    },
    "project-reconciliation": {
      text: "Below that is every clocked day no [[hwr]] or [[cr]] of the project covers. Reports with clearly different hours are listed too.",
    },
    "planning-absence": {
      text: "A whole day of absence from personnel planning does not count as a missing day in Review. The cell shows the absence instead, and Review can enter one itself.",
    },
  },
  typed: {
    note: "Picked up material from hall 2",
  },
}
